// Stripe Checkout redirect lander.
//
// User flow:
//   1. Lands here from Stripe with ?session_id=cs_xxx
//   2. We poll GET /api/payments/checkout/status/{session_id}
//   3. On `paid`, hydrate the booking reference from the response
//      and redirect to the existing /booking-success/:reference page
//   4. On `expired` or terminal failure, show a clean error CTA
//
// Polling: every 2s, max 5 attempts (10s) per the playbook. After timeout
// we show a "still processing" message + email CTA so the user is never
// stuck in a spinner forever.

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, WarningCircle, CircleNotch } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const POLL_MS = 2000;
const MAX_POLLS = 5;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking | success | failed | timeout
  const [error, setError] = useState(null);
  const attempts = useRef(0);

  useSEO({ title: "Confirming Payment | TheCityPair", noindex: true });

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      setError("Missing payment session id.");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          // Look up the booking reference, then redirect to the confirmation
          // page. We don't store any booking data in this component.
          if (data.booking_id) {
            try {
              const { data: booking } = await api.get(`/bookings/by-id/${data.booking_id}`);
              if (booking?.reference) {
                nav(`/booking-success/${booking.reference}`, { replace: true });
                return;
              }
            } catch (e) {
              // Booking lookup failed — fall through to the generic success
              // screen. Common when the booking record hasn't finished
              // writing yet at the moment Stripe redirects us back.
              if (process.env.NODE_ENV !== "production") console.debug("by-id lookup:", e);
            }
          }
          setPhase("success");
          return;
        }
        if (data.status === "expired") {
          setPhase("failed");
          setError("Payment session expired. Please try again.");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_POLLS) {
          setPhase("timeout");
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        setPhase("failed");
        setError("Could not confirm payment status.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>
      <div className="px-6 md:px-12 lg:px-24 py-20 max-w-2xl mx-auto" data-testid="payment-return">
        {phase === "checking" && (
          <>
            <CircleNotch size={42} className="text-[#C29B62] animate-spin" />
            <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-6 text-[#1C1C1C]">
              Confirming your payment…
            </h1>
            <p className="text-[#666460] mt-4">This usually takes a few seconds. Please don't close the page.</p>
          </>
        )}
        {phase === "success" && (
          <>
            <CheckCircle size={48} weight="fill" className="text-[#C29B62]" />
            <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-6 text-[#1C1C1C]">
              Payment received.
            </h1>
            <p className="text-[#666460] mt-4">
              Our concierge will be in touch shortly with your e-tickets.
            </p>
            <Link to="/" className="btn-gold text-xs tracking-[0.16em] uppercase mt-8 inline-block">
              Back to Home
            </Link>
          </>
        )}
        {(phase === "failed" || phase === "timeout") && (
          <>
            <WarningCircle size={42} weight="fill" className="text-rose-500" />
            <h1 className="font-serif text-4xl md:text-5xl tracking-tighter mt-6 text-[#1C1C1C]">
              {phase === "failed" ? "Payment could not be confirmed." : "Still processing your payment…"}
            </h1>
            <p className="text-[#666460] mt-4">
              {phase === "failed"
                ? (error || "Please try the booking flow again, or contact concierge if you were charged.")
                : "We're waiting for Stripe to finalise things. If the charge already happened, our concierge will email you shortly."}
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/" className="btn-gold text-xs tracking-[0.16em] uppercase">Back to Home</Link>
              <a href="mailto:concierge@thecitypair.com" className="text-xs tracking-[0.16em] uppercase px-5 py-2.5 border border-[#E0DDD6] inline-flex items-center bg-white text-[#1C1C1C]">
                Contact Concierge
              </a>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
